import { Injectable } from '@angular/core';
import { BaseSearchStrategy } from './base-strategy';
import { SearchableItem } from '../models/searchable-item.model';
import {
  ScoredMatch,
  StrategyContext,
  StrategyId,
} from '../models/search-strategy.model';
import {
  fuzzyMatch,
  levenshteinDistance,
} from '../utils/fuzzy-search.utils';

@Injectable({ providedIn: 'root' })
export class KeywordMatchStrategy extends BaseSearchStrategy {
  readonly id: StrategyId = 'keyword';
  readonly defaultWeight = 80;
  private readonly fuzzyThreshold = 0.7;

  override score<T>(
    item: SearchableItem<T>,
    ctx: StrategyContext
  ): ScoredMatch | null {
    const q = ctx.normalizedQuery;
    const keywords = this.normalizedKeywords(item);
    if (keywords.length === 0) return null;

    let best = 0;
    let evidence: string | undefined;
    for (const kw of keywords) {
      // 1) substring match — full credit
      if (kw.includes(q) || q.includes(kw)) {
        const sub = this.defaultWeight;
        if (sub > best) { best = sub; evidence = `substr:${kw}`; }
        continue;
      }
      // 2) fuzzy match
      if (fuzzyMatch(q, kw, this.fuzzyThreshold)) {
        const fz = this.defaultWeight * 0.75;
        if (fz > best) { best = fz; evidence = `fuzzy:${kw}`; }
        continue;
      }
      // 3) levenshtein <= 2 for tolerance on short typos
      if (q.length >= 4 && levenshteinDistance(q, kw) <= 2) {
        const lev = this.defaultWeight * 0.6;
        if (lev > best) { best = lev; evidence = `lev:${kw}`; }
      }
    }
    return this.match(this.id, best, evidence);
  }
}
